import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from 'components/buttons';
import { ShowHide } from "components/layout";
import { DefectCaptureHasDefectsPreview } from "./hasDefects/DefectCaptureHasDefectsPreview";
import { DefectCapturePreview } from "./defectCapture/DefectCapturePreview";

export function TemplateBuilderDefectCapturePreview({ question, answer, onChange }) {
    const [showDefectCapture, setShowDefectCapture] = useState(false);

    const handleShowDefectCapture = () => setShowDefectCapture(true);
    const handleCloseDefectCapture = () => setShowDefectCapture(false);

    const value = useMemo(() => {
        return answer;
    }, [answer]);
    
    useEffect(() => {
        validate(value)
    }, [value]);

    useEffect(() => {
        const defaultValue = {
            $answerType: "DefectCapture",
            questionId: question.id,
            defects: [{ comment: "", media: [] }],
            hasDefects: null
        };

        const validationErrors = validate(value || defaultValue);
        const isValid = validationErrors === "";

        if (!isValid) {
            handleChange(defaultValue);
        }
    }, [question]);

    const handleChange = (newValue) => {
        const validationErrors = validate(newValue);
        const isValid = validationErrors === "";

        const newAnswer = {
            $answerType: "DefectCapture",
            questionId: question.id,
            defects: newValue.defects || [],
            hasDefects: newValue.hasDefects,
            isAnswered: isValid,
            notValid: !isValid,
            error: validationErrors
        };

        onChange(newAnswer);
    };

    const handleHasDefectsChange = (newValue) => {
        const updatedValue = {
            ...answer,
            hasDefects: newValue,
            defects: newValue === false ? [] : answer.defects
        };

        handleChange(updatedValue);
    };

    const handleDefectCaptureChange = (newValue) => {
        const updatedValue = {
            ...answer,
            defects: newValue.defects || []
        };

        handleChange(updatedValue);
    };
    
    const validate = useCallback((newValue) => {
        if (newValue?.hasDefects === false) {
            return "";
        } else if (newValue?.hasDefects === undefined || newValue?.hasDefects === null) {
            return "Please select if there are defects or not.";
        }

        if (!newValue?.defects || newValue.defects.length === 0) {
            return "At least one defect is required.";
        }

        for (const defect of newValue.defects) {
            if (!defect.defectComment || defect.defectComment.trim() === "") {
                return "Each defect must have a comment.";
            }
            
            if (!Array.isArray(defect.media) || defect.media.length < question.minNumberOfMedia) {
                return `Each defect must have at least ${question.minNumberOfMedia} media items.`;
            }

            if (question.maxNumberOfMedia && defect.media.length > question.maxNumberOfMedia) {
                return `Each defect can have a maximum of ${question.maxNumberOfMedia} media items.`;
            }
        }

        return "";
    }, []);

    return (
        <div className="check-preview-form-field">
            {showDefectCapture && (
                <DefectCapturePreview
                    question={question}
                    answer={answer}
                    onChange={handleDefectCaptureChange}
                    onClose={handleCloseDefectCapture}
                />
            )}
            {!showDefectCapture && (
                <div>
                    <DefectCaptureHasDefectsPreview
                        answer={value.hasDefects}
                        onChange={handleHasDefectsChange}
                    />
                    <ShowHide
                        evaluator={value.hasDefects === true}
                        show={
                            <Button
                                onClick={handleShowDefectCapture}
                                className="check-preview-defects-capture-button spacer-top"
                                text={"Add defects"}
                                icon="add"
                            />
                        }
                    />
                </div>
            )}
        </div>
    );
}

TemplateBuilderDefectCapturePreview.propTypes = {
    answer: PropTypes.object,
    question: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
};